import {
    ButtonGroup,
    DatePicker,
    Emoji,
    getResponsibilityColors,
    IButtonGroupItemProps,
    Notice,
    Popover,
    PopoverHeader,
    PopoverItem,
    ResponsibilityBadge,
    Variables,
} from '@enter/core';
import { mdiCalendarMonthOutline, mdiChevronDown } from '@mdi/js';
import { observer } from 'mobx-react';
import * as React from 'react';
import { localToday, RESPONSIBILITY_RANGE_PRESETS, toLocalDate } from 'stores/claim-responsibility-range.util';
import {
    categoryIcon,
    categoryLabel,
    categoryShadePercent,
    formatClaimCount,
    formatCompactMoney,
    formatShare,
    NONE_RESPONSIBILITY,
    ResponsibilityMetric,
} from 'stores/claim-responsibility-report.util';
import type { ClaimResponsibilityStore } from 'stores/claim-responsibility.store';
import * as S from './responsibility-summary.styles';

const METRIC_BUTTONS: Record<ResponsibilityMetric, IButtonGroupItemProps> = {
    charges: { label: '$ Charges' },
    count: { label: '# Claims' },
};

const claims = (count: number): string => `${formatClaimCount(count)} claim${count === 1 ? '' : 's'}`;

/** Charges first or claims first, by the metric toggle. */
function figures(totals: { count: number; chargeCents: number }, metric: ResponsibilityMetric): [string, string] {
    const money = formatCompactMoney(totals.chargeCents);
    return metric === 'count' ? [claims(totals.count), money] : [money, claims(totals.count)];
}

/** The group colour stepped toward white by rank — the largest category is the group colour itself. */
function categoryShade(groupColor: string, rank: number, count: number): string {
    return `color-mix(in srgb, ${groupColor} ${categoryShadePercent(rank, count)}%, ${Variables.Swatches.White})`;
}

export interface IResponsibilitySummaryProps {
    store: ClaimResponsibilityStore;
}

/**
 * "Active Claims By Responsibility" — the card above the provider claims table (FE-151).
 *
 * Every number is derived from `GET /provider/report/responsibility` in `ClaimResponsibilityStore`;
 * this component only lays it out. A failed request shows the reason and no figures at all, never
 * zeros (NS-3).
 */
export const ResponsibilitySummary: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => {
    React.useEffect(() => {
        void store.load();
    }, [store]);

    const { summary, metric, loadedRange, error } = store;
    const hasFigures = !!loadedRange;

    return (
        <S.Panel size="large">
            <S.Header>
                <S.TitleBlock>
                    <S.Title>Active Claims By Responsibility</S.Title>
                    {hasFigures ? (
                        <S.Totals>
                            {formatCompactMoney(summary.total.chargeCents)} · {claims(summary.total.count)} ·{' '}
                            {metric === 'count' ? 'by claims' : 'by charges'}
                        </S.Totals>
                    ) : (
                        !error && <S.Totals>Loading…</S.Totals>
                    )}
                </S.TitleBlock>
                <S.Controls>
                    <RangePicker store={store} />
                    <ButtonGroup
                        variant="track"
                        buttons={[METRIC_BUTTONS.charges, METRIC_BUTTONS.count]}
                        selected={METRIC_BUTTONS[metric]}
                        onSelect={button => store.setMetric(button === METRIC_BUTTONS.count ? 'count' : 'charges')}
                    />
                    {hasFigures && summary.none && (
                        <S.NonePill active={store.selectedType === NONE_RESPONSIBILITY} onClick={() => store.toggleType(NONE_RESPONSIBILITY)}>
                            <Emoji type="thumbsUp" />
                            <span>
                                <strong>{formatClaimCount(summary.none.count)}</strong> None ·{' '}
                                {formatCompactMoney(summary.none.chargeCents)} · complete
                            </span>
                        </S.NonePill>
                    )}
                </S.Controls>
            </S.Header>

            {error ? (
                <Notice tone="attention">{error}</Notice>
            ) : (
                <S.Body stale={store.loading && hasFigures}>
                    <GroupBar store={store} />
                    {hasFigures && <GroupChips store={store} />}
                    {hasFigures && store.selectedGroup && <CategoryBreakdown store={store} />}
                </S.Body>
            )}

            <S.Footnote>
                Active claims of every claim type, by create date. Responsibility is each claim&apos;s current state, so
                totals can differ from the table below.
            </S.Footnote>
        </S.Panel>
    );
});

const GroupBar: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => (
    <S.Bar height={22}>
        {store.loadedRange &&
            store.rankedGroups.map(({ item, share }) => (
                <S.Segment
                    key={item.type}
                    share={share}
                    fill={getResponsibilityColors(item.type).color}
                    dimmed={!!store.selectedType && store.selectedType !== item.type}
                    active={store.selectedType === item.type}
                    onClick={() => store.toggleType(item.type)}>
                    <S.VisuallyHidden>
                        {item.type} {formatShare(share)}
                    </S.VisuallyHidden>
                </S.Segment>
            ))}
    </S.Bar>
));

const GroupChips: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => (
    <S.Chips>
        {store.rankedGroups.map(({ item, share }) => {
            const [primary, secondary] = figures(item, store.metric);
            return (
                <S.GroupChip key={item.type} active={store.selectedType === item.type} onClick={() => store.toggleType(item.type)}>
                    <ResponsibilityBadge size="large" responsibility={item.type} />
                    <S.Primary>{primary}</S.Primary>
                    <S.Secondary>
                        {formatShare(share)} · {secondary}
                    </S.Secondary>
                </S.GroupChip>
            );
        })}
    </S.Chips>
));

const CategoryBreakdown: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => {
    const group = store.selectedGroup!;
    const groupColor = getResponsibilityColors(group.type).color;
    const ranked = store.rankedCategories;
    const shade = (rank: number): string => categoryShade(groupColor, rank, ranked.length);

    return (
        <S.Breakdown>
            <S.BreakdownTitle>
                {group.type} · category types<span>{store.metric === 'count' ? 'by claims' : 'by charges'}</span>
            </S.BreakdownTitle>
            <S.Bar height={8}>
                {ranked.map(({ item, share }, rank) => (
                    <S.Segment
                        key={item.code}
                        share={share}
                        fill={shade(rank)}
                        dimmed={!!store.selectedCategory && store.selectedCategory !== item.code}
                        active={store.selectedCategory === item.code}
                        onClick={() => store.toggleCategory(item.code)}>
                        <S.VisuallyHidden>
                            {categoryLabel(item.code)} {formatShare(share)}
                        </S.VisuallyHidden>
                    </S.Segment>
                ))}
            </S.Bar>
            <S.Chips>
                {ranked.map(({ item, share }, rank) => {
                    const active = store.selectedCategory === item.code;
                    const [first, second] = figures(item, store.metric);
                    return (
                        <S.CategoryChip key={item.code} active={active} onClick={() => store.toggleCategory(item.code)}>
                            <S.Swatch fill={shade(rank)} />
                            <S.CategoryIcon path={categoryIcon(item.code)} active={active} />
                            <S.CategoryText>
                                <S.CategoryLabel active={active}>{categoryLabel(item.code)}</S.CategoryLabel>
                                <S.CategoryStat>
                                    {formatShare(share)} · {first} · {second}
                                </S.CategoryStat>
                                <S.CategoryCode>{item.code}</S.CategoryCode>
                            </S.CategoryText>
                        </S.CategoryChip>
                    );
                })}
            </S.Chips>
        </S.Breakdown>
    );
});

/**
 * Presets and a custom range in core's `Popover`. The custom fields are core's `DatePicker`, with
 * the days outside the store's limits disabled in its calendar, so an out-of-range day is hard to
 * pick at all; what still gets through (a typed date) is caught by the store's validation and shown
 * under them. The calendar opens in a MUI popover outside this menu's DOM — core `Popover` already
 * ignores clicks there (`.MuiPopover-root`, `.rdp`), so picking a day does not close the menu.
 */
const RangePicker: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => (
    <Popover
        align="right"
        selectable={true}
        renderButton={() => (
            <S.RangeButton variant="outlined" size="compact">
                <S.RangeButtonIcon path={mdiCalendarMonthOutline} />
                <S.RangeButtonPrefix>Created</S.RangeButtonPrefix>
                {store.rangeLabel}
                <S.RangeButtonIcon path={mdiChevronDown} />
            </S.RangeButton>
        )}>
        <PopoverHeader>Claim create date</PopoverHeader>
        {RESPONSIBILITY_RANGE_PRESETS.map(preset => (
            <PopoverItem key={preset.key} selected={store.preset === preset.key} onClick={() => store.setPreset(preset.key)}>
                {preset.label}
            </PopoverItem>
        ))}
        <CustomRange store={store} />
    </Popover>
));

/**
 * `Popover` clones every child with its menu props (`closeMenu`, `pinned`, …). This takes them and
 * drops them, so they are not spread onto a DOM element, and so typing a date does not close the menu.
 */
const CustomRange: React.FC<IResponsibilitySummaryProps> = observer(({ store }) => {
    const limits = store.inputLimits;
    // The calendar works in LOCAL days; the store in `YYYY-MM-DD`. `toLocalDate` / `localToday` convert
    // without the UTC shift `new Date('YYYY-MM-DD')` or core's UTC `formatDate` would introduce.
    const toDay = (date: Date | undefined): string => (date ? localToday(date) : '');
    const startMax = toLocalDate(limits.startMax);
    const endMax = toLocalDate(limits.endMax);
    return (
        <S.CustomRange>
            <S.Caption>Custom range · max 1 year</S.Caption>
            <S.DateInputs>
                <S.DateField>
                    Start
                    <DatePicker
                        value={store.customStart}
                        onDateSelected={date => store.setCustomStart(toDay(date))}
                        customDayPickerProps={{ disabled: { after: startMax }, endMonth: startMax }}
                    />
                </S.DateField>
                <S.DateField>
                    End
                    <DatePicker
                        value={store.customEnd}
                        onDateSelected={date => store.setCustomEnd(toDay(date))}
                        customDayPickerProps={{
                            disabled: [{ before: toLocalDate(limits.endMin) }, { after: endMax }],
                            endMonth: endMax,
                        }}
                    />
                </S.DateField>
            </S.DateInputs>
            {store.rangeError && <S.RangeError role="alert">{store.rangeError}</S.RangeError>}
            <S.Caption>Responsibility reflects current claim state.</S.Caption>
        </S.CustomRange>
    );
});
